<template>
  <div class="feedback-box content plagiarism-tab d-flex">
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="loader-container d-flex justify-content-center py-4"
    >
      <infinity-loading />
    </div>

    <!-- Upgrade Prompt for Non-Pro/Premium Plans -->
    <div v-else-if="!hasPlagiarism" class="plagiarism-upgrade-prompt">
      <flags-empty
        tab="plagiarism"
        @clickFlagsManually="onClickFlagsManually"
      />
    </div>

    <!-- Plagiarism Content for Pro/Premium Plans -->
    <template v-else>
      <!-- Score Header Box -->
      <div class="plagiarism-score-box">
        <div class="score-info">
          <!--<span class="score-label paragraph-m">Average plagiarism score:</span>
          <span class="score-value" :class="scoreClass">
            {{ plagiarismScore }}%
          </span>-->
          <span
            class="score-description paragraph-s"
            :class="elementClass('score')"
            >{{ scoreDescription }}</span
          >
        </div>
        <div
          class="plagiarism-badge caption-s medium"
          :class="elementClass('badge')"
        >
          <eg-icon :icon="badgeIcon" size="small" />
          <span>{{ badgeText }}</span>
        </div>
      </div>

      <!-- Info Box About Plagiarism Detection -->
      <!-- <next-steps-box
        title="About Plagiarism Detection"
        icon="bulb-light"
        :text="aboutPlagiarismText"
        :is-list="true"
      /> -->

      <!-- No Plagiarism State -->
      <without-plagiarism v-if="!hasPlagiarism" />

      <!-- Plagiarism Detected State -->
      <plagiarism-detected
        v-else
        :essay-content="essay.content"
        :similar-essays="flag.similarEssays"
        :plagiarism-score="plagiarismScore"
        :selected-index="selectedSentenceIndex"
        @select-sentence="onSelectSentence"
        @updateHighlights="$emit('updateHighlights', $event)"
      />
    </template>
  </div>
</template>

<script>
import FlagsEmpty from "@/components/essay/essay/flags/v2/flags_empty.vue";
// import NextStepsBox from "@/components/essay/essay/v3/next_steps_box.vue";
import WithoutPlagiarism from "@/components/essay/essay/components/without_plagiarism.vue";
import PlagiarismDetected from "@/components/essay/essay/components/plagiarism_detected.vue";
import UpgradePlanModal from "@/components/global/modals/upgrade_plan_modal.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import { createFlagsManually } from "@/api";

export default {
  name: "EssayTabPlagiarism",

  components: {
    // NextStepsBox,
    WithoutPlagiarism,
    PlagiarismDetected,
    FlagsEmpty,
    InfinityLoading,
  },

  inject: ["accountProvider"],

  props: {
    essay: {
      type: Object,
      required: true,
    },
    activeHighlightIndex: {
      type: Number,
      default: -1,
    },
  },

  data() {
    return {
      // Currently selected sentence index (-1 = all highlighted)
      selectedSentenceIndex: -1,
      isLoading: false,
    };
  },

  computed: {
    flag() {
      return this.essay?.flags?.find((f) => f.type === "plagiarism") || null;
    },

    hasPlagiarism() {
      return !!this.flag;
    },

    plagiarismScore() {
      if (!this.flag) {
        return 0;
      }

      return this.flag.summary?.plagiarismScore || 0;
    },

    scoreDescription() {
      if (this.plagiarismScore < 30) {
        return this.$t("essays.plagiarismDetails.noMatchingContent");
      } else if (this.plagiarismScore <= 70) {
        return "Moderate similarities noticed. A quick review of the submissions is recommended. ";
      }

      return "Significant portions of the submissions match. A thorough review is highly recommended. ";
    },

    badgeIcon() {
      return this.plagiarismScore < 30 ? "check" : "information";
    },

    badgeText() {
      if (this.plagiarismScore < 30) {
        return this.$t("essays.plagiarismDetails.noPlagiarismDetected");
      } else if (this.plagiarismScore <= 70) {
        return this.$t("essays.plagiarismDetails.potentialPlagiarism");
      }

      return this.$t("essays.plagiarismDetails.plagiarismDetected");
    },

    aboutPlagiarismText() {
      return "Internal checks compare against all student essays within your class.\nGlobal checks scan against academic journals, websites, and published content.";
    },
  },

  methods: {
    onSelectSentence(index) {
      // Toggle selection - if same index clicked, deselect (show all)
      this.selectedSentenceIndex =
        this.selectedSentenceIndex === index ? -1 : index;
      this.$emit("updateActiveHighlightIndex", this.selectedSentenceIndex);
    },

    upgradePlan() {
      this.$egModal.show("upgrade-plan-modal", {
        component: UpgradePlanModal,
        props: {
          currentPlan: this.accountProvider?.subscription?.plan,
        },
      });
    },
    // TODO: after release 4.7 move this inside the flags component and
    // replace in ai with correct type in the request according to the props
    async onClickFlagsManually() {
      if (!(this.$store.getters.proPlan || this.$store.getters.premiumPlan)) {
        this.$bvModal.show("upgrade-modal");

        return;
      }

      this.isLoading = true;

      try {
        const {
          data: { flags },
        } = await createFlagsManually(this.essay.id, [
          {
            type: "plagiarism",
          },
        ]);

        this.$emit("updateEssay", {
          ...this.essay,
          flags: [...this.essay.flags, ...flags],
        });
      } catch (error) {
        console.error("Error creating flags manually:", error);
        this.$showToastError(
          error.response?.data?.error ||
            this.$t("essays.plagiarismDetails.failedToCheck")
        );
      } finally {
        this.isLoading = false;
      }
    },
    // elementName is either `score` or `badge`
    elementClass(elementName = "score") {
      if (this.plagiarismScore < 30) {
        return `${elementName}-success`;
      } else if (this.plagiarismScore <= 70) {
        return `${elementName}-warning`;
      }

      return `${elementName}-danger`;
    },
  },
};
</script>

<style lang="scss" scoped>
.plagiarism-tab {
  gap: 1rem;
  flex-direction: column;
  padding: 1.5rem !important;
  white-space: normal;
}

.plagiarism-score-box {
  display: flex;
  padding: 1.5rem;
  align-items: center;
  gap: 0.5rem;
  border-radius: var(--radius-rd-300, 8px);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);

  .score-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  .score-label {
    font-weight: var(--typography-font-weight-fw-400, 400);
  }

  .score-value {
    font-size: var(--typography-font-size-fs-400);
    font-weight: var(--typography-font-weight-fw-700, 700);
  }
  .score-success {
    color: var(--color-text-semantic-onSuccess, #308c4a);
  }

  .score-low {
    color: var(--color-text-neutral-secondary, #566370);
  }

  .score-warning {
    color: var(--color-text-semantic-onWarning, #d76812);
  }

  .score-danger {
    color: var(--color-text-semantic-onError, #dc3545);
  }

  .score-description {
    font-weight: var(--typography-font-weight-fw-500, 500);
  }
}

.plagiarism-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 3px 8px;
  border-radius: var(--radius-rd-500, 16px);
  white-space: nowrap;

  &.badge-success {
    background: var(--color-surface-semantic-success-default, #28a745);
    color: #fff;
  }

  &.badge-danger {
    background: var(--color-surface-semantic-error-error, #dc3545);
    color: #fff;
  }
}
.loader-container,
.plagiarism-upgrade-prompt {
  height: calc(100vh - 262px - var(--maintenance-height));
}

.plagiarism-upgrade-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  padding: 0.5rem 0.5rem 8.25rem;
  width: 100%;
}

.upgrade-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
  max-width: 440px;
  .paragraph-m {
    font-weight: var(--typography-font-weight-fw-400);
  }
}
</style>
